import React, { useState, useEffect } from 'react';
import imageBannerService, { ImageBanner } from '../services/imageBannerService';
import './ImageCarousel.css';

interface ImageCarouselProps {
  className?: string;
}

const ImageCarousel: React.FC<ImageCarouselProps> = ({ className = '' }) => {
  const [imageBanners, setImageBanners] = useState<ImageBanner[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<ImageBanner | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imageRefreshKey, setImageRefreshKey] = useState(Date.now());
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchImageBanners(true);
    
    // Poll for updates every 10 seconds to reflect changes made in edit mode
    const pollInterval = setInterval(() => {
      fetchImageBanners(false); // Don't show loading on background refresh
    }, 10000);
    
    return () => clearInterval(pollInterval);
  }, []);

  // Preload all images
  useEffect(() => {
    if (imageBanners.length > 0) {
      imageBanners.forEach((banner, index) => {
        const img = new Image();
        img.src = `${imageBannerService.getImageUrl(banner.image)}?t=${imageRefreshKey}`;
        img.onload = () => {
          setLoadedImages(prev => new Set(prev).add(index));
        };
      });
    }
  }, [imageBanners, imageRefreshKey]);

  // Auto-play functionality
  useEffect(() => {
    if (imageBanners.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === imageBanners.length - 1 ? 0 : prevIndex + 1
        );
      },8000); // Change slide every  8 seconds

      return () => clearInterval(interval);
    }
  }, [imageBanners.length]);

  const fetchImageBanners = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const banners = await imageBannerService.getVisibleImageBanners();
      console.log('Fetched banners:', banners.length, 'banners at', new Date().toLocaleTimeString());
      setImageBanners(banners);
      setError(null);
      setImageRefreshKey(Date.now()); // Update refresh key when banners are fetched
    } catch (err) {
      console.error('Failed to fetch image banners:', err);
      if (showLoading) setError('Failed to load carousel images');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const nextSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === imageBanners.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? imageBanners.length - 1 : prevIndex - 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const handleImageClick = (image: ImageBanner) => {
    // Always open popup to show image with title and description
    setSelectedImage(image);
  };

  const closeImagePopup = () => {
    setSelectedImage(null);
  };

  if (loading) {
    return (
      <div className={`image-carousel ${className}`}>
        <div className="carousel-loading">Loading carousel...</div>
      </div>
    );
  }

  if (error || imageBanners.length === 0) {
    return (
      <div className={`image-carousel ${className}`}>
        <div className="carousel-error">
          {error || 'No images available'}
        </div>
      </div>
    );
  }

  const currentImage = imageBanners[currentIndex];

  return (
    <>
      <div className={`image-carousel ${className}`}>
        <div className="carousel-container">
          {/* Main Image */}
          <div className="carousel-slide">
            <img
              key={currentIndex}
              src={`${imageBannerService.getImageUrl(currentImage.image)}?t=${imageRefreshKey}`}
              alt={currentImage.title || 'Banner image'}
              className={`carousel-image ${currentImage.link ? 'cursor-pointer' : ''}`}
              onClick={() => handleImageClick(currentImage)}
              style={{
                opacity: loadedImages.has(currentIndex) ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out'
              }}
              onLoad={() => {
                setLoadedImages(prev => new Set(prev).add(currentIndex));
              }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/assets/images/banner-image.jpg';
                setLoadedImages(prev => new Set(prev).add(currentIndex));
              }}
            />
            <div className="carousel-overlay">
              <div className="carousel-content">
                <h3 className="carousel-title">{currentImage.title}</h3>
                <p className="carousel-description">
                  {currentImage.description}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          {imageBanners.length > 1 && (
            <>
              <button 
                className="carousel-nav carousel-prev" 
                onClick={prevSlide}
                aria-label="Previous image"
              >
                &#8249;
              </button>
              <button 
                className="carousel-nav carousel-next" 
                onClick={nextSlide}
                aria-label="Next image"
              >
                &#8250;
              </button>
            </>
          )}

          {/* Dots Indicator */}
          {imageBanners.length > 1 && (
            <div className="carousel-dots">
              {imageBanners.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Image Popup Modal */}
      {selectedImage && (
        <div className="image-popup-overlay" onClick={closeImagePopup}>
          <div className="image-popup-container">
            <button 
              className="image-popup-close" 
              onClick={closeImagePopup}
              aria-label="Close image"
            >
              &times;
            </button>
            <img
              src={`${imageBannerService.getImageUrl(selectedImage.image)}?t=${imageRefreshKey}`}
              alt={selectedImage.title || 'Banner image'}
              className="image-popup-image"
              onClick={(e) => e.stopPropagation()}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/assets/images/banner-image.jpg';
              }}
            />
            <div className="image-popup-info">
              <h3 className="image-popup-title">{selectedImage.title}</h3>
              {selectedImage.description && (
                <p className="image-popup-description">
                  {selectedImage.description}
                </p>
              )}
              {selectedImage.link && (
                <a 
                  href={selectedImage.link}
                  target={selectedImage.link.startsWith('http') ? '_blank' : '_self'}
                  rel={selectedImage.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="image-popup-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  Learn More →
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageCarousel;
